// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: assistant cards. The system account's DMs (meeting reminders,
// ClickUp activity, agendas, digests, reminders) arrive as rich embeds whose
// provider is "Konvo:<kind>". This draws them as one calm card: a source row
// with a real logo, the title, a short body, optional sections, a status chip
// with context, and link buttons. The structured parts ride in well-known
// embed fields (status, meta, action[:icon], event:<ms>); every other field is a section.
// event fields are a timeline: several updates to one ClickUp task collected on one card. Interactive
// cards add a card id, button[:style] fields (action://name) and an optional input field;
// a press posts to the API, which redraws the card into this same message.
// Embeds the system account sent before cards existed carry no provider;
// they are read back from their footer, title and description so history
// looks the same as new messages.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {EmbedProps} from '@app/features/channel/components/embeds/channel_embed/ChannelEmbedShared';
import {EmbedLink} from '@app/features/channel/components/embeds/channel_embed/EmbedLink';
import styles from '@app/features/channel/components/embeds/channel_embed/KonvoCard.module.css';
import {AgentAskCard} from '@app/features/konvo_agents/components/AgentAskCard';
import {AssistantActionCard} from '@app/features/konvo_assistant/components/AssistantActionCard';
import {AssistantStructuredResponse} from '@app/features/konvo_assistant/components/AssistantStructuredResponse';
import {AssistantCardFrame} from '@app/features/konvo_assistant/components/chat_chrome/AssistantCardMenu';
import {AssistantChatScopeContext} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeContext';
import {AssistantConnectCard} from '@app/features/konvo_engagement/components/AssistantConnectCard';
import {NudgeCard} from '@app/features/konvo_engagement/components/NudgeCard';
import {FeedbackCard} from '@app/features/konvo_feedback/components/FeedbackCard';
import {MondayReportCard} from '@app/features/konvo_reports/components/MondayReportCard';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {
	MarkdownContext,
	type MarkdownParseOptions,
} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {http} from '@app/features/platform/transport/RestTransport';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {FluxerIcon} from '@app/features/ui/components/icons/FluxerIcon';
import {OutlookCalendarLogo} from '@app/features/ui/components/icons/OutlookCalendarLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {MessageEmbedTypes} from '@fluxer/constants/src/ChannelConstants';
import {AGENT_ASK_PROVIDER} from '@fluxer/schema/src/domains/konvo/Agents';
import {feedbackAskId} from '@fluxer/schema/src/domains/konvo/AppFeedback';
import {assistantChatKindOfEmbed} from '@fluxer/schema/src/domains/konvo/AssistantChat';
import {
	ASSISTANT_CONNECT_PROVIDER,
	decodeNudgeCardFields,
	NUDGE_CARD_FIELD_PREFIX,
	NUDGE_CARD_PROVIDER,
} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {
	ASSISTANT_RESPONSE_FIELD_PREFIX,
	ASSISTANT_RESPONSE_PROVIDER,
	decodeAssistantResponseFields,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import type {EmbedField, MessageEmbed} from '@fluxer/schema/src/domains/message/EmbedSchemas';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type CSSProperties, type FC, useContext, useMemo, useState} from 'react';

const PROVIDER_PREFIX = 'Konvo:';
const ACTION_PREFIX = 'action:';
const ACTION_LINK = /^\[([^\]]+)\]\((\S+)\)$/;
const BUTTON_PREFIX = 'button';
const BUTTON_LINK = /^\[([^\]]+)\]\(action:\/\/([a-z0-9_-]{1,40})\)$/i;
const INPUT_PREFIX = 'input:';
const EVENT_PREFIX = 'event:';
const CARD_FAILED_DESCRIPTOR = msg({
	message: 'That did not go through. Try again.',
	comment: 'Toast when an assistant card button fails.',
});
const LOGO_SIZE = 14;
/** Emoji and their joiners, which the old builders put in front of titles and links. */
const PICTOGRAPHS = /\p{Extended_Pictographic}|\uFE0F|\u200D/gu;

export function isKonvoCard(embed: MessageEmbed, message?: Message): boolean {
	if (embed.provider?.name?.startsWith(PROVIDER_PREFIX)) return true;
	return message?.author.system === true && embed.type === MessageEmbedTypes.RICH && Boolean(embed.title);
}

interface CardAction {
	label: string;
	url: string;
	icon: string | null;
}

interface CardSection {
	name: string;
	value: string;
}

interface CardButton {
	label: string;
	action: string;
	style: 'primary' | 'danger' | 'plain';
}

interface CardEvent {
	at: number;
	body: string;
}

interface CardInput {
	name: string;
	placeholder: string;
}

interface CardView {
	kind: string | null;
	source: string;
	title: string | null;
	url: string | null;
	body: string | null;
	sections: Array<CardSection>;
	timeline: Array<CardEvent>;
	status: string | null;
	meta: string | null;
	actions: Array<CardAction>;
	cardId: string | null;
	buttons: Array<CardButton>;
	input: CardInput | null;
	done: boolean;
}

type CardParts = Pick<
	CardView,
	'status' | 'meta' | 'sections' | 'timeline' | 'actions' | 'cardId' | 'buttons' | 'input' | 'done'
>;

function stripPictographs(text: string): string {
	return text
		.replace(PICTOGRAPHS, '')
		.replace(/\s{2,}/g, ' ')
		.trim();
}

function readFields(fields: ReadonlyArray<EmbedField> | undefined): CardParts {
	const parts: CardParts = {
		status: null,
		meta: null,
		sections: [],
		timeline: [],
		actions: [],
		cardId: null,
		buttons: [],
		input: null,
		done: false,
	};
	for (const field of fields ?? []) {
		if (field.name.startsWith(ASSISTANT_RESPONSE_FIELD_PREFIX) || field.name.startsWith(NUDGE_CARD_FIELD_PREFIX)) {
			// Encoded card JSON: never shown as text (a nudge that cannot be read falls back to its title and summary).
		} else if (field.name === 'status') {
			parts.status = field.value;
		} else if (field.name === 'meta') {
			parts.meta = field.value;
		} else if (field.name === 'card') {
			parts.cardId = field.value.trim();
		} else if (field.name === 'done') {
			parts.done = true;
		} else if (field.name.startsWith(EVENT_PREFIX)) {
			const at = Number(field.name.slice(EVENT_PREFIX.length));
			if (Number.isSafeInteger(at)) parts.timeline.push({at, body: field.value});
		} else if (field.name.startsWith(INPUT_PREFIX)) {
			parts.input = {name: field.name.slice(INPUT_PREFIX.length), placeholder: field.value.trim()};
		} else if (field.name === BUTTON_PREFIX || field.name.startsWith(`${BUTTON_PREFIX}:`)) {
			const match = BUTTON_LINK.exec(field.value.trim());
			if (!match) continue;
			const style = field.name.slice(BUTTON_PREFIX.length + 1);
			parts.buttons.push({
				label: match[1],
				action: match[2],
				style: style === 'primary' || style === 'danger' ? style : 'plain',
			});
		} else if (field.name === 'action' || field.name.startsWith(ACTION_PREFIX)) {
			const match = ACTION_LINK.exec(field.value.trim());
			if (!match) continue;
			const icon = field.name.startsWith(ACTION_PREFIX) ? field.name.slice(ACTION_PREFIX.length) : null;
			parts.actions.push({label: match[1], url: match[2], icon: icon || null});
		} else {
			parts.sections.push({name: field.name, value: field.value});
		}
	}
	parts.timeline.sort((a, b) => a.at - b.at);
	return parts;
}

function taggedView(embed: MessageEmbed): CardView {
	return {
		kind: embed.provider?.name?.slice(PROVIDER_PREFIX.length) ?? null,
		source: embed.author?.name ?? 'Konvo',
		title: embed.title ?? null,
		url: embed.url ?? null,
		body: embed.description ?? null,
		...readFields(embed.fields),
	};
}

/** Footer text of the pre-card builders to the kind and source they meant. */
function legacyKind(footer: string): {kind: string; source: string} {
	if (/outlook calendar/i.test(footer)) return {kind: 'calendar', source: 'Outlook Calendar'};
	if (/daily agenda/i.test(footer)) return {kind: 'agenda', source: 'Konvo · Daily agenda'};
	if (/end.of.day/i.test(footer)) return {kind: 'digest', source: 'Konvo · End of day'};
	if (/time summary/i.test(footer)) return {kind: 'time', source: 'ClickUp · Time tracking'};
	if (/clickup/i.test(footer)) return {kind: 'clickup', source: 'ClickUp'};
	if (/remind/i.test(footer)) return {kind: 'reminder', source: 'Konvo · Reminder'};
	return {kind: 'konvo', source: footer || 'Konvo'};
}

function legacyActionIcon(label: string): string | null {
	if (/teams/i.test(label)) return 'teams';
	if (/konvo|jump/i.test(label)) return 'konvo';
	if (/clickup/i.test(label)) return 'clickup';
	if (/outlook/i.test(label)) return 'outlook';
	return null;
}

const BOLD_LINK = /\*\*\[([^\]]+)\]\((\S+)\)\*\*/g;
const SECTION_HEAD = /^\*\*([^*\n]+)\*\*\n([\s\S]+)$/;
const STARTS_LINE = /^Starts \*\*in ([^*]+)\*\*(?: · (.+))?$/;

function legacyView(embed: MessageEmbed): CardView {
	const footer = embed.footer?.text ?? '';
	const {kind, source} = legacyKind(footer);
	const view: CardView = {
		kind,
		source,
		title: embed.title ? stripPictographs(embed.title) : null,
		url: embed.url ?? null,
		body: null,
		sections: [],
		timeline: [],
		status: null,
		meta: null,
		actions: [],
		cardId: null,
		buttons: [],
		input: null,
		done: false,
	};
	if (kind === 'clickup') {
		const status = footer.split('·')[1]?.trim();
		if (status) view.status = status;
	}
	if (kind === 'reminder' && footer) view.meta = footer;
	const bodyLines: Array<string> = [];
	for (const block of (embed.description ?? '').split(/\n{2,}/)) {
		const text = block.trim();
		if (!text) continue;
		const links = [...text.matchAll(BOLD_LINK)];
		if (links.length > 0 && text.replace(BOLD_LINK, '').trim() === '') {
			for (const link of links) {
				const label = stripPictographs(link[1]);
				view.actions.push({label, url: link[2], icon: legacyActionIcon(label)});
			}
			continue;
		}
		const section = SECTION_HEAD.exec(text);
		if (section) {
			view.sections.push({name: stripPictographs(section[1]), value: stripPictographs(section[2])});
			continue;
		}
		const starts = STARTS_LINE.exec(text);
		if (starts) {
			view.status = `In ${starts[1]}`;
			if (starts[2]) view.meta = starts[2];
			continue;
		}
		bodyLines.push(stripPictographs(text));
	}
	view.body = bodyLines.length > 0 ? bodyLines.join('\n\n') : null;
	return view;
}

function eventTime(at: number): string {
	return new Date(at).toDateString() === new Date().toDateString()
		? DateUtils.getFormattedTime(at)
		: DateUtils.getFormattedCompactDateTime(at);
}

/** A card's updates, oldest first, so the newest sits right above the status chip. */
const Timeline: FC<{events: Array<CardEvent>; markdown: MarkdownParseOptions}> = ({events, markdown}) => (
	<ol className={styles.timeline} data-flx="channel.embeds.konvo-card.timeline">
		{events.map((event, index) => (
			<li
				key={`${event.at}-${index}`}
				className={clsx(styles.timelineEvent, index === events.length - 1 && styles.timelineLatest)}
				data-flx="channel.embeds.konvo-card.timeline-event"
			>
				<span className={styles.timelineDot} aria-hidden="true" />
				<div className={styles.timelineBody}>
					<SafeMarkdown
						content={event.body}
						options={markdown}
						data-flx="channel.embeds.konvo-card.timeline-markdown"
					/>
				</div>
				<time className={styles.timelineTime} dateTime={new Date(event.at).toISOString()}>
					{eventTime(event.at)}
				</time>
			</li>
		))}
	</ol>
);

/** The logo for a card kind or an action icon name; the Konvo mark otherwise. */
const BrandMark: FC<{name: string | null; className: string}> = ({name, className}) => {
	switch (name) {
		case 'clickup':
		case 'time':
			return (
				<span className={className} data-flx="channel.embeds.konvo-card.brand-mark.clickup">
					<ClickUpLogo size={LOGO_SIZE} />
				</span>
			);
		case 'calendar':
			return (
				<span className={className} data-flx="channel.embeds.konvo-card.brand-mark.calendar">
					<OutlookCalendarLogo size={LOGO_SIZE} />
				</span>
			);
		case 'outlook':
			return (
				<span className={className} data-flx="channel.embeds.konvo-card.brand-mark.outlook">
					<OutlookLogo size={LOGO_SIZE} />
				</span>
			);
		case 'teams':
			return (
				<span className={className} data-flx="channel.embeds.konvo-card.brand-mark.teams">
					<TeamsLogo size={LOGO_SIZE} />
				</span>
			);
		default:
			return (
				<span className={className} data-flx="channel.embeds.konvo-card.brand-mark.konvo">
					<FluxerIcon width={LOGO_SIZE} height={LOGO_SIZE} />
				</span>
			);
	}
};

const CardButtons: FC<{cardId: string; buttons: Array<CardButton>; input: CardInput | null; done: boolean}> = ({
	cardId,
	buttons,
	input,
	done,
}) => {
	const {i18n} = useLingui();
	const [text, setText] = useState('');
	const [pending, setPending] = useState<string | null>(null);
	const press = async (action: string) => {
		if (pending || done) return;
		setPending(action);
		try {
			await http.post(Endpoints.KONVO_CARD_ACTION(cardId, action), {body: {input: text || undefined}});
			setText('');
		} catch {
			ToastCommands.error(i18n._(CARD_FAILED_DESCRIPTOR));
		} finally {
			setPending(null);
		}
	};
	const primary = buttons.find((button) => button.style === 'primary') ?? buttons[0];
	return (
		<div className={styles.interactive} data-flx="channel.embeds.konvo-card.interactive">
			{input && !done && (
				<input
					className={styles.input}
					value={text}
					placeholder={input.placeholder}
					maxLength={2000}
					onChange={(event) => setText(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && primary) {
							event.preventDefault();
							void press(primary.action);
						}
					}}
					data-flx="channel.embeds.konvo-card.input"
				/>
			)}
			<div className={styles.actions} data-flx="channel.embeds.konvo-card.buttons">
				{buttons.map((button) => (
					<button
						key={button.action}
						type="button"
						className={clsx(
							styles.action,
							styles.button,
							button.style === 'primary' && styles.actionPrimary,
							button.style === 'danger' && styles.actionDanger,
							pending === button.action && styles.buttonPending,
						)}
						disabled={done || pending !== null}
						aria-busy={pending === button.action || undefined}
						onClick={() => void press(button.action)}
						data-flx="channel.embeds.konvo-card.button"
					>
						{button.label}
					</button>
				))}
			</div>
		</div>
	);
};

export const KonvoCard: FC<Pick<EmbedProps, 'embed' | 'message'>> = observer(({embed, message}) => {
	const assistantChatId = useContext(AssistantChatScopeContext);
	const content = <KonvoCardContent embed={embed} message={message} />;
	// KONVO: in the Assistant chat every card gets the ⋯ menu (History, Pin, Mute, Archive); one per
	// message, on its first card. The Agents card draws its ⋯ in its own header; a dashboard adds
	// these to its own ⋯ menu.
	if (assistantChatId !== message.channelId) return content;
	const firstCard = message.embeds.find((candidate) => isKonvoCard(candidate, message));
	if (firstCard !== embed) return content;
	const kind = assistantChatKindOfEmbed(embed);
	return (
		<AssistantCardFrame message={message} kind={kind} cornerButton={kind !== 'agents' && kind !== 'dashboard'}>
			{content}
		</AssistantCardFrame>
	);
});

const KonvoCardContent: FC<Pick<EmbedProps, 'embed' | 'message'>> = observer(({embed, message}) => {
	const {i18n} = useLingui();
	const tagged = embed.provider?.name?.startsWith(PROVIDER_PREFIX) ?? false;
	const view = useMemo(() => (tagged ? taggedView(embed) : legacyView(embed)), [embed, tagged]);
	const structuredResponse = useMemo(
		() => (embed.provider?.name === ASSISTANT_RESPONSE_PROVIDER ? decodeAssistantResponseFields(embed.fields) : null),
		[embed],
	);
	const nudgeCard = useMemo(
		() => (embed.provider?.name === NUDGE_CARD_PROVIDER ? decodeNudgeCardFields(embed.fields) : null),
		[embed],
	);
	const feedbackAsk = useMemo(() => feedbackAskId(embed), [embed]);
	const stamp = embed.timestamp ? DateUtils.getRelativeDateString(embed.timestamp, i18n) : null;
	const accent = embed.color ? `#${embed.color.toString(16).padStart(6, '0')}` : null;
	const markdown = {
		context: MarkdownContext.RESTRICTED_EMBED_DESCRIPTION,
		messageId: message.id,
		channelId: message.channelId,
	};
	if (view.cardId && view.source === 'Konvo · Action preview')
		return <AssistantActionCard key={view.cardId} cardId={view.cardId} />;
	if (nudgeCard) return <NudgeCard card={nudgeCard} channelId={message.channelId} />;
	if (feedbackAsk)
		return (
			<FeedbackCard
				askId={feedbackAsk}
				title={embed.title ?? null}
				fallback={embed.description ?? null}
				channelId={message.channelId}
			/>
		);
	if (embed.provider?.name === ASSISTANT_CONNECT_PROVIDER)
		return <AssistantConnectCard channelId={message.channelId} />;
	if (structuredResponse)
		return (
			<AssistantStructuredResponse
				response={structuredResponse}
				responseKey={message.id}
				onAction={(prompt) =>
					void MessageCommands.send(message.channelId, {
						content: prompt,
						nonce: SnowflakeUtils.fromTimestamp(Date.now()),
					})
				}
			/>
		);
	if (
		view.cardId &&
		!view.done &&
		(view.kind === 'monday' ||
			(view.source.includes('Monday Madness') && view.buttons.some((button) => button.action === 'send')))
	)
		return <MondayReportCard key={view.cardId} cardId={view.cardId} />;
	const plain = (
		<article
			className={clsx(styles.card, markupStyles.markup)}
			style={accent ? ({'--konvo-card-accent': accent} as CSSProperties) : undefined}
			data-search-highlight-scope="message"
			data-flx="channel.embeds.konvo-card"
		>
			<div className={styles.source} data-flx="channel.embeds.konvo-card.source">
				<span className={styles.sourceTile} aria-hidden="true">
					<BrandMark name={view.kind} className={styles.sourceLogo} />
				</span>
				<span className={styles.sourceName} data-flx="channel.embeds.konvo-card.source-name">
					{view.source}
				</span>
				{stamp && (
					<span className={styles.stamp} data-flx="channel.embeds.konvo-card.stamp">
						{stamp}
					</span>
				)}
			</div>
			{view.title && (
				<div className={styles.title} data-flx="channel.embeds.konvo-card.title">
					{view.url ? (
						<EmbedLink url={view.url} className={styles.titleLink} data-flx="channel.embeds.konvo-card.title-link">
							{view.title}
						</EmbedLink>
					) : (
						view.title
					)}
				</div>
			)}
			{view.timeline.length > 1 ? (
				<Timeline events={view.timeline} markdown={markdown} />
			) : (
				view.body && (
					<div className={styles.body} data-flx="channel.embeds.konvo-card.body">
						<SafeMarkdown content={view.body} options={markdown} data-flx="channel.embeds.konvo-card.markdown" />
					</div>
				)
			)}
			{view.sections.map((section, index) => (
				<div key={`${section.name}-${index}`} className={styles.section} data-flx="channel.embeds.konvo-card.section">
					<div className={styles.sectionLabel} data-flx="channel.embeds.konvo-card.section-label">
						{section.name}
					</div>
					<div className={styles.sectionBody} data-flx="channel.embeds.konvo-card.section-body">
						<SafeMarkdown content={section.value} options={markdown} data-flx="channel.embeds.konvo-card.markdown--2" />
					</div>
				</div>
			))}
			{(view.status || view.meta) && (
				<div className={styles.meta} data-flx="channel.embeds.konvo-card.meta">
					{view.status && (
						<span className={styles.chip} data-flx="channel.embeds.konvo-card.chip">
							<span className={styles.chipDot} data-flx="channel.embeds.konvo-card.chip-dot" />
							{view.status}
						</span>
					)}
					{view.meta && <span data-flx="channel.embeds.konvo-card.meta-text">{view.meta}</span>}
				</div>
			)}
			{view.actions.length > 0 && (
				<div className={styles.actions} data-flx="channel.embeds.konvo-card.actions">
					{view.actions.map((action, index) => (
						<EmbedLink
							key={`${action.url}-${index}`}
							url={action.url}
							className={clsx(styles.action, index === 0 && styles.actionPrimary)}
							data-flx="channel.embeds.konvo-card.action"
						>
							{action.icon && <BrandMark name={action.icon} className={styles.actionIcon} />}
							{action.label}
						</EmbedLink>
					))}
				</div>
			)}
			{view.cardId && view.buttons.length > 0 && (
				<CardButtons cardId={view.cardId} buttons={view.buttons} input={view.input} done={view.done} />
			)}
		</article>
	);
	// An agent's question a colleague was asked (Ask a colleague): drawn from the ask, else plain.
	if (embed.provider?.name === AGENT_ASK_PROVIDER && view.cardId)
		return <AgentAskCard key={view.cardId} askId={view.cardId} statusKey={view.status ?? ''} fallback={plain} />;
	return plain;
});
